'use client';
/*
 * Documentation:
 * Button — https://app.subframe.com/6fc7f5fbb098/library?component=Button_3b777358-b86b-40af-9327-891efc6826fe
 */

import * as SubframeCore from '@subframe/core';
import React from 'react';
import * as SubframeUtils from '../utils';

interface ButtonRootProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  disabled?: boolean;
  variant?:
    | 'brand-primary'
    | 'brand-gradient'
    | 'brand-gradient-inactive'
    | 'brand-secondary'
    | 'brand-tertiary'
    | 'brand-inverse'
    | 'neutral-primary'
    | 'neutral-secondary'
    | 'neutral-tertiary'
    | 'destructive-primary'
    | 'destructive-secondary'
    | 'destructive-tertiary'
    | 'inverse';
  size?: 'x-large' | 'large' | 'medium' | 'small';
  children?: React.ReactNode;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  loading?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

const ButtonRoot = React.forwardRef<HTMLButtonElement, ButtonRootProps>(function ButtonRoot(
  {
    disabled = false,
    variant = 'brand-primary',
    size = 'medium',
    children,
    icon = null,
    iconRight = null,
    loading = false,
    className,
    type = 'button',
    ...otherProps
  }: ButtonRootProps,
  ref,
) {
  return (
    <button
      className={SubframeUtils.twClassNames(
        'group/3b777358 bg-brand-700 hover:bg-brand-600 active:bg-brand-700 flex h-10 cursor-pointer items-center justify-center gap-2 rounded-[8px] border-none px-4 text-left shadow-none transition-all duration-200 disabled:cursor-default hover:disabled:cursor-default active:disabled:cursor-default',
        {
          'disabled:bg-neutral-200 hover:disabled:bg-neutral-200 active:disabled:bg-neutral-200':
            variant !== 'brand-gradient-inactive' && !variant.startsWith('brand-'),
          'disabled:opacity-80 disabled:shadow-none disabled:saturate-[0.7]':
            variant.startsWith('brand-') && variant !== 'brand-gradient-inactive',
          'h-8 w-auto flex-row flex-nowrap gap-1.5 px-3 py-0': size === 'small',
          'h-9 w-auto gap-2.5 px-6 py-0': size === 'medium',
          'h-11 w-auto gap-2.5 px-6 py-0': size === 'large',
          'h-12 w-auto gap-3 px-8 py-0': size === 'x-large',
          'from-brand-700 via-brand-600 to-brand-500 hover:from-brand-600 hover:via-brand-500 hover:to-brand-400 bg-gradient-to-r shadow-md hover:shadow-lg':
            variant === 'brand-gradient',
          'from-brand-700/40 via-brand-600/40 to-brand-500/40 cursor-default bg-gradient-to-r opacity-60 shadow-none':
            variant === 'brand-gradient-inactive',
          'bg-default-background border border-solid border-neutral-200 hover:bg-neutral-50 active:bg-neutral-100':
            variant === 'brand-inverse',
          'bg-transparent hover:bg-[var(--color-bg-inverse-overlay)] active:bg-[var(--color-bg-inverse-overlay)]':
            variant === 'inverse',
          'hover:bg-error-50 active:bg-error-100 bg-transparent':
            variant === 'destructive-tertiary',
          'bg-error-50 hover:bg-error-100 active:bg-error-50': variant === 'destructive-secondary',
          'bg-error-600 hover:bg-error-500 active:bg-error-600': variant === 'destructive-primary',
          'bg-transparent hover:bg-neutral-100 active:bg-neutral-200':
            variant === 'neutral-tertiary',
          'bg-default-background active:bg-default-background border border-solid border-neutral-200 hover:bg-neutral-50':
            variant === 'neutral-secondary',
          'bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-100':
            variant === 'neutral-primary',
          'hover:bg-brand-50 active:bg-brand-100 bg-transparent': variant === 'brand-tertiary',
          'bg-brand-50 hover:bg-brand-100 active:bg-brand-50': variant === 'brand-secondary',
        },
        className,
      )}
      ref={ref}
      type={type}
      disabled={disabled}
      {...otherProps}
    >
      {icon ? (
        <SubframeCore.IconWrapper
          className={SubframeUtils.twClassNames('subframe-icon-wrapper text-base', {
            'text-text-inverse':
              !variant.startsWith('brand-') ||
              variant === 'brand-primary' ||
              variant === 'brand-gradient' ||
              variant === 'brand-tertiary' ||
              variant === 'inverse',
            'group-disabled/3b777358:text-neutral-400': !variant.startsWith('brand-'),
            'group-disabled/3b777358:text-text-inverse-secondary': variant.startsWith('brand-'),
            hidden: loading,
            'text-sm': size === 'small',
            'text-xl': size === 'large',
            'text-text-inverse-secondary': variant === 'brand-gradient-inactive',
            'text-[var(--bg-inverse)]': variant === 'brand-inverse',
            'text-error-700':
              variant === 'destructive-tertiary' || variant === 'destructive-secondary',
            'text-brand-600': variant === 'brand-secondary',
            'text-neutral-700':
              variant === 'neutral-tertiary' ||
              variant === 'neutral-secondary' ||
              variant === 'neutral-primary',
          })}
        >
          {icon}
        </SubframeCore.IconWrapper>
      ) : null}
      <div
        className={SubframeUtils.twClassNames(
          'hidden h-4 w-4 flex-none items-center justify-center gap-2',
          { flex: loading, 'h-3 w-3 flex-none': size === 'small' },
        )}
      >
        <SubframeCore.Loader
          className={SubframeUtils.twClassNames('text-caption font-caption', {
            'text-text-inverse':
              !variant.startsWith('brand-') ||
              variant === 'brand-primary' ||
              variant === 'brand-gradient' ||
              variant === 'brand-tertiary' ||
              variant === 'inverse',
            'group-disabled/3b777358:text-neutral-400': !variant.startsWith('brand-'),
            'group-disabled/3b777358:text-text-inverse-secondary': variant.startsWith('brand-'),
            'text-caption font-caption inline-block tracking-normal': loading,
            'text-caption font-caption': size === 'small',
            'text-[var(--background)]': variant === 'brand-inverse',
            'text-error-700':
              variant === 'destructive-tertiary' || variant === 'destructive-secondary',
            'text-neutral-700':
              variant === 'neutral-tertiary' ||
              variant === 'neutral-secondary' ||
              variant === 'neutral-primary',
            'text-brand-600': variant === 'brand-tertiary' || variant === 'brand-secondary',
          })}
        />
      </div>
      {children ? (
        <span
          className={SubframeUtils.twClassNames('text-body font-medium whitespace-nowrap', {
            'text-text-inverse':
              !variant.startsWith('brand-') ||
              variant === 'brand-primary' ||
              variant === 'brand-gradient' ||
              variant === 'brand-secondary' ||
              variant === 'brand-tertiary' ||
              variant === 'inverse',
            'group-disabled/3b777358:text-neutral-400': !variant.startsWith('brand-'),
            'group-disabled/3b777358:text-text-inverse-secondary': variant.startsWith('brand-'),
            hidden: loading,
            'text-caption font-caption': size === 'small',
            'text-heading-3 font-medium': size === 'large',
            'text-text-inverse-secondary': variant === 'brand-gradient-inactive',
            'text-[var(--bg-inverse)]': variant === 'brand-inverse',
            'text-error-700':
              variant === 'destructive-tertiary' || variant === 'destructive-secondary',
            'text-neutral-700':
              variant === 'neutral-tertiary' ||
              variant === 'neutral-secondary' ||
              variant === 'neutral-primary',
            'text-brand-600': variant === 'brand-tertiary' || variant === 'brand-secondary',
          })}
        >
          {children}
        </span>
      ) : null}
      {iconRight ? (
        <SubframeCore.IconWrapper
          className={SubframeUtils.twClassNames('subframe-icon-wrapper text-base', {
            'text-text-inverse':
              !variant.startsWith('brand-') ||
              variant === 'brand-primary' ||
              variant === 'brand-gradient' ||
              variant === 'brand-tertiary' ||
              variant === 'inverse',
            'group-disabled/3b777358:text-neutral-400': !variant.startsWith('brand-'),
            'group-disabled/3b777358:text-text-inverse-secondary': variant.startsWith('brand-'),
            'text-sm': size === 'small',
            'text-xl': size === 'large',
            'text-2xl': size === 'x-large',
            'text-text-inverse-secondary': variant === 'brand-gradient-inactive',
            'text-[var(--bg-inverse)]': variant === 'brand-inverse',
            'text-error-700':
              variant === 'destructive-tertiary' || variant === 'destructive-secondary',
            'text-neutral-700':
              variant === 'neutral-tertiary' ||
              variant === 'neutral-secondary' ||
              variant === 'neutral-primary',
            'text-brand-600': variant === 'brand-tertiary' || variant === 'brand-secondary',
          })}
        >
          {iconRight}
        </SubframeCore.IconWrapper>
      ) : null}
    </button>
  );
});

export const Button = ButtonRoot;
